import {
  Award,
  Bell,
  Calendar,
  FileText,
  LayoutDashboard,
  LogOut,
  type LucideIcon,
  Maximize,
  MessageSquare,
  Receipt,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';

import { versionDuServeur } from '../api/version';
import { sectionsPour } from './navigation';
import { useSession } from './SessionContext';
import { DarkModeToggle } from '../components/DarkModeToggle';

const ICONES: Record<string, LucideIcon> = {
  'layout-dashboard': LayoutDashboard,
  users: Users,
  calendar: Calendar,
  'file-text': FileText,
  receipt: Receipt,
  'shield-check': ShieldCheck,
  award: Award,
  'message-square': MessageSquare,
  bell: Bell,
};

/**
 * Titre affiché dans l'en-tête : il vient du MENU, jamais d'une seconde liste.
 * Le rôle est passé explicitement : le titre doit décrire ce que CE compte voit,
 * pas une entrée qu'il n'a pas le droit d'ouvrir.
 */
function entreeCourante(chemin: string, role: 'prestataire' | 'admin' | undefined) {
  const entrees = sectionsPour(role).flatMap((section) => section.entrees);
  return (
    entrees.find((entree) => entree.chemin === chemin) ??
    entrees.find((entree) => entree.chemin !== '/' && chemin.startsWith(entree.chemin)) ??
    entrees.find((entree) => entree.chemin === '/')
  );
}

function titreDeLaPage(chemin: string, role: 'prestataire' | 'admin' | undefined): string {
  if (chemin.startsWith('/a-venir')) return 'Bientôt disponible';
  // Livrer un écran sans toucher à cette fonction laissait l'en-tête annoncer
  // « Tableau de bord » au-dessus de la liste des clients (constaté à l'écran).
  return entreeCourante(chemin, role)?.libelle ?? 'Tableau de bord';
}

/** Sous-titres des écrans livrés ; les autres retombent sur celui du tableau de bord. */
const SOUS_TITRES: Record<string, string> = {
  '/': 'Votre activité du moment en un regard.',
  '/interventions': 'Gérez votre planning et vos rapports d’intervention.',
  '/clients': 'Les clients dont vous suivez le registre de sécurité.',
  '/administration': 'Les comptes de la plateforme : rôles, activation, volumes.',
  '/journal': 'Qui a créé, modifié ou suspendu quoi, et quand.',
  '/mon-compte': 'Vos informations et le changement de votre mot de passe.',
};

function sousTitreDeLaPage(chemin: string): string {
  if (chemin.startsWith('/a-venir'))
    return 'Cette partie de l’espace prestataire n’est pas encore livrée.';
  return SOUS_TITRES[chemin] ?? 'Votre activité du moment en un regard.';
}

/** « 2026-10-25 » → « 25 octobre 2026 », sans passer par le fuseau du poste. */
function dateFrancaise(iso: string): string {
  const mois = [
    'janvier',
    'février',
    'mars',
    'avril',
    'mai',
    'juin',
    'juillet',
    'août',
    'septembre',
    'octobre',
    'novembre',
    'décembre',
  ];
  const [annee = '', m = '', j = ''] = iso.split('-');
  const index = Number(m) - 1;
  if (annee === '' || m === '' || j === '') return iso;
  const jour = Number(j);
  // Le 1er s'écrit « 1er », les autres « 2 », « 3 »…
  return `${jour === 1 ? '1er' : jour} ${mois[index] ?? m} ${annee}`;
}

/** Deux initiales au plus, pour la pastille du compte. */
function initiales(nom: string | undefined): string {
  if (!nom) return '?';
  const mots = nom.trim().split(/\s+/).filter(Boolean);
  return mots
    .slice(0, 2)
    .map((mot) => mot[0]?.toUpperCase() ?? '')
    .join('');
}

export function Layout() {
  const chemin = useLocation().pathname;
  const { utilisateur, deconnecter } = useSession();
  /** Le jour vient du SERVEUR : tant qu'il n'a pas répondu, on n'affiche rien. */
  const [jourServeur, setJourServeur] = useState<string>();

  useEffect(() => {
    let vivant = true;
    void versionDuServeur().then((version) => {
      if (vivant && version?.aujourdHui) setJourServeur(version.aujourdHui);
    });
    return () => {
      vivant = false;
    };
  }, []);

  return (
    <div className="bg-[var(--app-background)] text-[var(--app-foreground)] h-screen flex overflow-hidden">
      <aside className="w-64 bg-white border-r border-slate-200 flex flex-col flex-shrink-0">
        <div className="h-20 flex flex-col justify-center px-5 border-b border-slate-200">
          <img
            src="/logo-registre.webp"
            alt="Registre-Sécurité"
            className="logo-marque h-7 w-auto self-start"
          />
          <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            Portail fournisseur
          </p>
        </div>

        <nav
          aria-label="Navigation principale"
          className="flex-1 overflow-y-auto py-4 px-3 text-sm font-medium"
        >
          {sectionsPour(utilisateur?.role).map((section) => (
            <div key={section.titre}>
              <div className="px-3 mt-6 first:mt-0 mb-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                {section.titre}
              </div>
              <ul className="space-y-1">
                {section.entrees.map((entree) => {
                  const Icone = ICONES[entree.icone] ?? Calendar;
                  return (
                    <li key={entree.chemin}>
                      <NavLink
                        to={entree.chemin}
                        end={entree.chemin === '/'}
                        className={({ isActive }) =>
                          [
                            'flex items-center gap-3 px-3 py-2 rounded-lg',
                            isActive
                              ? 'bg-red-50 text-red-700'
                              : 'text-slate-600 hover:bg-slate-50',
                          ].join(' ')
                        }
                      >
                        <Icone className="w-4 h-4" aria-hidden="true" />
                        <span className="flex-1">{entree.libelle}</span>
                        {!entree.disponible && (
                          <span className="text-[10px] uppercase tracking-wide text-slate-400">
                            bientôt
                          </span>
                        )}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-xs flex-shrink-0"
              aria-hidden="true"
            >
              {initiales(utilisateur?.nom)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">
                {utilisateur?.nom ?? 'Compte'}
              </p>
              <p className="text-xs text-slate-500 truncate">{utilisateur?.email ?? ''}</p>
            </div>
            <button
              type="button"
              onClick={() => void deconnecter()}
              aria-label="Déconnexion"
              title="Déconnexion"
              className="p-1.5 rounded-lg text-slate-400 hover:text-red-700 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-8 flex-shrink-0">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-semibold text-slate-800">
              {titreDeLaPage(chemin, utilisateur?.role)}
            </h1>
            <span className="text-sm text-slate-500 hidden md:block">
              {sousTitreDeLaPage(chemin)}
            </span>
          </div>
          <div className="flex items-center gap-6 text-sm text-slate-500">
            <div className="text-right hidden sm:block">
              <p className="font-medium text-slate-800">
                {jourServeur ? dateFrancaise(jourServeur) : ''}
              </p>
              <p className="text-xs">Espace prestataire</p>
            </div>
            <div className="flex items-center gap-2 border-l pl-6 border-slate-200">
              <DarkModeToggle />
              <Maximize className="w-4 h-4" aria-hidden="true" />
              <Bell className="w-4 h-4" aria-hidden="true" />
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
